import React, { useState } from 'react';
import { collection, doc, setDoc, getDoc, serverTimestamp, query, where, getDocs, limit } from 'firebase/firestore';
import { db, storage } from '../../firebaseConfig';
import { ref, uploadBytesResumable, getDownloadURL, uploadBytes } from 'firebase/storage';
import { useNavigate, Link } from 'react-router-dom';
import { Editor } from '@tinymce/tinymce-react';
import { slugify } from '../../utils/slugify';
import 'tinymce/tinymce';
import 'tinymce/icons/default';
import 'tinymce/themes/silver';
import 'tinymce/models/dom';
import 'tinymce/skins/ui/oxide/skin.min.css';
import 'tinymce/skins/content/default/content.min.css';
import 'tinymce/plugins/lists';
import 'tinymce/plugins/link';
import 'tinymce/plugins/image';
import 'tinymce/plugins/table';
import 'tinymce/plugins/preview';
import 'tinymce/plugins/code';
import 'tinymce/plugins/fullscreen';

const CATEGORY_OPTIONS = [
  'Edukasi Ruqyah',
  'Gangguan & Keluhan',
  'Thibbun Nabawi',
  'Umum'
];

export default function AdminPostCreate() {
  const navigate = useNavigate();

  const [title, setTitle] = useState('');
  const [excerpt, setExcerpt] = useState('');
  const [content, setContent] = useState('');
  const [category, setCategory] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [status, setStatus] = useState('draft');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [progress, setProgress] = useState(0);

  const uploadImageIfNeeded = () => {
    return new Promise((resolve, reject) => {
      if (!imageFile) return resolve('');
      const storageRef = ref(storage, `article-images/${Date.now()}_${imageFile.name}`);
      const uploadTask = uploadBytesResumable(storageRef, imageFile);

      uploadTask.on('state_changed', snapshot => {
        const pct = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
        setProgress(pct);
      }, err => reject(err), async () => {
        const url = await getDownloadURL(uploadTask.snapshot.ref);
        resolve(url);
      });
    });
  };

  const handleEditorImageUpload = async (blobInfo) => {
    const filename = `article-content/${Date.now()}_${blobInfo.filename()}`;
    const storageRef = ref(storage, filename);
    await uploadBytes(storageRef, blobInfo.blob());
    const url = await getDownloadURL(storageRef);
    return url;
  };

  const createUniqueSlug = async (titleValue) => {
    const base = slugify(titleValue) || `artikel-${Date.now().toString(36)}`;
    for (let attempt = 0; attempt < 5; attempt += 1) {
      const candidate = attempt === 0 ? base : `${base}-${Date.now().toString(36).slice(-4)}`;
      const docRef = doc(db, 'articles', candidate);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) continue;
      const q = query(collection(db, 'articles'), where('slug', '==', candidate), limit(1));
      const existing = await getDocs(q);
      if (existing.empty) return candidate;
    }
    return `${base}-${Date.now().toString(36).slice(-6)}`;
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setError('');

    if (!title.trim()) {
      setError('Judul wajib diisi');
      return;
    }
    if (!category.trim()) {
      setError('Kategori wajib diisi');
      return;
    }

    setLoading(true);
    try {
      const uploadedUrl = await uploadImageIfNeeded();
      const finalImage = uploadedUrl || imageUrl || '';
      const slug = await createUniqueSlug(title);

      const docRef = doc(db, 'articles', slug);
      await setDoc(docRef, {
        title,
        excerpt,
        content,
        category,
        image: finalImage,
        slug,
        status,
        date: serverTimestamp(),
        views: 0
      });
      navigate('/admin/post');
    } catch (err) {
      setError('Gagal menyimpan artikel: ' + err.message);
      setLoading(false);
    }
  };

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <div>
          <h2>Buat Artikel Baru</h2>
          <p className="text-muted-small">Lengkapi informasi artikel sebelum diterbitkan.</p>
        </div>
      </div>

      <div className="admin-card">
        <form onSubmit={handleSubmit}>
        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}

        <div className="mb-3">
          <label className="form-label">Judul *</label>
          <input
            type="text"
            className="form-control"
            value={title}
            onChange={e => setTitle(e.target.value)}
            disabled={loading}
            required
          />
        </div>

        <div className="mb-3">
          <label className="form-label">Excerpt (ringkasan)</label>
          <textarea
            className="form-control"
            value={excerpt}
            onChange={e => setExcerpt(e.target.value)}
            rows={2}
            disabled={loading}
          />
        </div>

        <div className="mb-3">
          <label className="form-label">Konten</label>
          <div className="admin-editor">
            <Editor
              value={content}
              onEditorChange={value => setContent(value)}
              init={{
                height: 380,
                menubar: false,
                plugins: 'lists link image table preview code fullscreen',
                toolbar:
                  'undo redo | blocks | fontfamily fontsize | bold italic underline | alignleft aligncenter alignright | ' +
                  'bullist numlist | table | link image | removeformat | preview fullscreen',
                toolbar_mode: 'sliding',
                content_style:
                  'body { font-family: Inter, Segoe UI, Arial, sans-serif; font-size: 16px; line-height: 1.6; }',
                skin: false,
                content_css: false,
                automatic_uploads: true,
                images_upload_handler: async (blobInfo) => handleEditorImageUpload(blobInfo),
                promotion: false,
                branding: false,
              }}
            />
          </div>
        </div>

        <div className="mb-3">
          <label className="form-label">Kategori *</label>
          <select
            className="form-select"
            value={category}
            onChange={e => setCategory(e.target.value)}
            disabled={loading}
            required
          >
            <option value="">Pilih kategori</option>
            {CATEGORY_OPTIONS.map(item => (
              <option key={item} value={item}>{item}</option>
            ))}
          </select>
          <div className="form-text">Gunakan kategori agar artikel konsisten untuk SEO.</div>
        </div>

        <div className="mb-3">
          <label className="form-label">Upload Gambar (optional)</label>
          <input
            type="file"
            accept="image/*"
            className="form-control"
            onChange={e => setImageFile(e.target.files[0])}
            disabled={loading}
          />
          <div className="form-text">Atau masukkan URL gambar di bawah</div>
        </div>

        <div className="mb-3">
          <label className="form-label">URL Gambar (jika pakai URL)</label>
          <input
            type="text"
            className="form-control"
            value={imageUrl}
            onChange={e => setImageUrl(e.target.value)}
            disabled={loading}
            placeholder="https://example.com/image.jpg"
          />
        </div>

        {progress > 0 && progress < 100 && (
          <div className="mb-3">Mengupload gambar: {progress}%</div>
        )}

        <div className="mb-3">
          <label className="form-label">Status *</label>
          <select
            className="form-select"
            value={status}
            onChange={e => setStatus(e.target.value)}
            disabled={loading}
            required
          >
            <option value="draft">Draf</option>
            <option value="published">Terbit</option>
          </select>
        </div>

        <div className="admin-form-actions">
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? 'Menyimpan...' : 'Simpan Artikel'}
          </button>
          <Link to="/admin/post" className="btn btn-outline-secondary">
            Batal
          </Link>
        </div>
      </form>
      </div>
    </div>
  );
} 
